import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  getDentalFindings,
  deleteDentalFinding,
  getTreatmentsForFinding,
  setTreatmentsForFinding,
} from "api/dentalMaster";
import { STATUS_OPTIONS } from "config/dentalMasterOptions";
import DentalFindingModal from "./DentalFindingModal";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import ExtraoralFindingMaster from "./ExtraoralFindingMaster";
import IntraoralFindingMaster from "./IntraoralFindingMaster";
import SpecialityTreatmentMaster from "../SpecialityTreatment/SpecialityTreatmentMaster";
import SpecialityProcedureMaster from "../SpecialityProcedure/SpecialityProcedureMaster";
import DentalWorkspaceMaster from "./DentalWorkspaceMaster";

const parseList = (res) =>
  res?.data?.data?.data || res?.data?.data || res?.data || [];

const TABS = [
  { key: "finding", label: "Findings" },
  { key: "mapping", label: "Finding → Treatments" },
  { key: "extraoral", label: "Extraoral Findings Master" },
  { key: "intraoral", label: "Intraoral Findings" },
  { key: "specialityTreatment", label: "Specialty Treatment Master" },
  { key: "specialityProcedure", label: "Procedure Master" },
  { key: "diagnosis", label: "Dental Diagnosis Master" },
];

const DentalMasterList = () => {
  const [activeTab, setActiveTab] = useState("finding");

  const [findings, setFindings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [formModal, setFormModal] = useState(false);
  const [selected, setSelected] = useState({});
  const [deleteId, setDeleteId] = useState("");

  // Tab 2 state
  const [findingId, setFindingId] = useState("");
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);
  const [savingRows, setSavingRows] = useState(false);
  const [dirty, setDirty] = useState(false);
  // Monotonic key source for unsaved rows, so add/remove/add never collides.
  const newRowSeq = useRef(0);

  const fetchFindings = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getDentalFindings({ page: 1, perPage: 1000 });
      const list = parseList(res);
      setFindings(list);
      setFindingId((current) => current || list[0]?._id || "");
    } catch (err) {
      toast.error(
        err?.response?.data?.message || "Error while fetching findings!"
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFindings();
  }, [fetchFindings]);

  const loadRows = useCallback(async (id) => {
    if (!id) {
      setRows([]);
      return;
    }
    setLoadingRows(true);
    try {
      const res = await getTreatmentsForFinding(id);
      setRows(
        parseList(res).map((row) => ({
          key: String(row._id),
          name: row.name ?? "",
          standardCost:
            row.standardCost === null || row.standardCost === undefined
              ? ""
              : String(row.standardCost),
        }))
      );
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Error loading treatments!");
    } finally {
      setLoadingRows(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === "mapping") loadRows(findingId);
  }, [activeTab, findingId, loadRows]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return findings.filter((item) => {
      if (term && !String(item.name || "").toLowerCase().includes(term)) {
        return false;
      }
      if (statusFilter === "active" && item.isActive === false) return false;
      if (statusFilter === "inactive" && item.isActive !== false) return false;
      return true;
    });
  }, [findings, search, statusFilter]);

  const paged = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage]
  );

  const handleFormClose = (type) => {
    setFormModal(false);
    setSelected({});
    if (type !== "close") fetchFindings();
  };

  const handleDelete = async () => {
    try {
      await deleteDentalFinding(deleteId);
      toast.success("Finding deleted successfully");
      setDeleteId("");
      fetchFindings();
      if (findingId === deleteId) setFindingId("");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Delete failed!");
    }
  };

  const updateRow = (index, field, value) => {
    setRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, [field]: value } : row))
    );
    setDirty(true);
  };

  const addRow = () => {
    newRowSeq.current += 1;
    setRows((prev) => [
      ...prev,
      { key: `new-${newRowSeq.current}`, name: "", standardCost: "" },
    ]);
    setDirty(true);
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
  };

  const handleSaveRows = async () => {
    const cleaned = [];
    const seen = new Set();

    for (const row of rows) {
      const name = row.name.trim();
      if (!name) continue;

      const cost = row.standardCost === "" ? 0 : Number(row.standardCost);
      if (!Number.isFinite(cost) || cost < 0) {
        toast.error(`"${name}" has an invalid cost`);
        return;
      }
      const key = name.toLowerCase();
      if (seen.has(key)) {
        toast.error(`"${name}" is listed more than once`);
        return;
      }
      seen.add(key);
      cleaned.push({ name, standardCost: cost });
    }

    setSavingRows(true);
    try {
      await setTreatmentsForFinding(findingId, cleaned);
      toast.success("Treatments saved successfully");
      loadRows(findingId);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed!");
    } finally {
      setSavingRows(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" fontWeight={600}>
          Global Dental Master
        </Typography>
        {activeTab === "finding" && (
          <Button
            variant="contained"
            onClick={() => {
              setSelected({});
              setFormModal(true);
            }}
          >
            Add Finding
          </Button>
        )}
      </Grid>

      <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        {TABS.map((tab) => (
          <Box
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            sx={{
              borderBottom:
                activeTab === tab.key
                  ? "2px solid #194AF5"
                  : "2px solid transparent",
              color: activeTab === tab.key ? "#194AF5" : "#7D8DC4",
              width: "max-content",
              padding: "0.5rem 1rem",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            {tab.label}
          </Box>
        ))}
      </Box>

      {activeTab === "diagnosis" ? (
        <DentalWorkspaceMaster />
      ) : activeTab === "specialityTreatment" ? (
        <SpecialityTreatmentMaster />
      ) : activeTab === "specialityProcedure" ? (
        <SpecialityProcedureMaster />
      ) : activeTab === "extraoral" ? (
        <ExtraoralFindingMaster />
      ) : activeTab === "intraoral" ? (
        <IntraoralFindingMaster />
      ) : activeTab === "finding" ? (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} sm={4}>
              <TextField
                size="small"
                fullWidth
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: "#F5F7FF" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>S.No</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Finding Name</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5 }}>
                      <CircularProgress size={24} />
                    </TableCell>
                  </TableRow>
                ) : paged.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      align="center"
                      sx={{ py: 5, color: "#7D8DC4" }}
                    >
                      No findings found.
                    </TableCell>
                  </TableRow>
                ) : (
                  paged.map((item, index) => (
                    <TableRow key={item._id} hover>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={item.isActive === false ? "Inactive" : "Active"}
                          color={item.isActive === false ? "default" : "success"}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex" }}>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelected(item);
                                setFormModal(true);
                              }}
                            >
                              <EditOutlinedIcon fontSize="small" color="primary" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteId(item._id)}
                            >
                              <DeleteOutlineIcon fontSize="small" color="error" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 280 }}>
              <InputLabel>Finding</InputLabel>
              <Select
                label="Finding"
                value={findingId}
                onChange={(e) => setFindingId(e.target.value)}
              >
                {findings.map((finding) => (
                  <MenuItem key={finding._id} value={finding._id}>
                    {finding.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ flex: 1 }} />

            {dirty && (
              <Typography variant="caption" sx={{ color: "#B45309" }}>
                Unsaved changes
              </Typography>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleSaveRows}
              disabled={savingRows || !dirty || !findingId}
              startIcon={
                savingRows ? <CircularProgress size={14} color="inherit" /> : null
              }
            >
              {savingRows ? "Saving..." : "Save"}
            </Button>
          </Box>

          {findings.length === 0 ? (
            <Typography variant="body2" sx={{ color: "#7D8DC4", py: 4 }}>
              Add a finding first.
            </Typography>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead sx={{ bgcolor: "#F5F7FF" }}>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>
                        Treatment Name
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 200 }}>
                        Cost
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, width: 80 }}>
                        Action
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loadingRows ? (
                      <TableRow>
                        <TableCell colSpan={3} align="center" sx={{ py: 5 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          align="center"
                          sx={{ py: 4, color: "#7D8DC4" }}
                        >
                          No treatments mapped to this finding yet.
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, index) => (
                        <TableRow key={row.key}>
                          <TableCell>
                            <TextField
                              variant="standard"
                              size="small"
                              fullWidth
                              value={row.name}
                              placeholder="e.g. Composite Filling"
                              onChange={(e) =>
                                updateRow(index, "name", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              size="small"
                              type="number"
                              fullWidth
                              value={row.standardCost}
                              inputProps={{ min: 0, step: "0.01" }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">₹</InputAdornment>
                                ),
                              }}
                              onChange={(e) =>
                                updateRow(index, "standardCost", e.target.value)
                              }
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Remove this treatment only">
                              <IconButton
                                size="small"
                                onClick={() => removeRow(index)}
                              >
                                <DeleteOutlineIcon fontSize="small" color="error" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addRow}
                sx={{ mt: 1.5 }}
                disabled={!findingId}
              >
                Add Treatment
              </Button>
            </>
          )}
        </>
      )}

      {formModal && (
        <DentalFindingModal
          open={formModal}
          onClose={handleFormClose}
          data={selected}
        />
      )}

      <ConfirmModal
        open={Boolean(deleteId)}
        type="dentalFinding"
        close={() => setDeleteId("")}
        confirm={handleDelete}
      />
    </Box>
  );
};

export default DentalMasterList;
